import { useEffect, useRef } from 'react';

const activeFocusLayers = [];

const registerFocusLayer = (token) => {
  const existingIndex = activeFocusLayers.indexOf(token);
  if (existingIndex >= 0) activeFocusLayers.splice(existingIndex, 1);
  activeFocusLayers.push(token);
};

const unregisterFocusLayer = (token) => {
  const index = activeFocusLayers.indexOf(token);
  if (index >= 0) activeFocusLayers.splice(index, 1);
};

const isTopFocusLayer = (token) => activeFocusLayers[activeFocusLayers.length - 1] === token;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'iframe:not([tabindex="-1"])',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getFocusableElements = (container) => Array.from(
  container?.querySelectorAll?.(FOCUSABLE_SELECTOR) || []
).filter((element) => element.getAttribute('aria-hidden') !== 'true');

/**
 * useFocusTrap — Extracts the focus trap logic from DrawerPanel for shared reuse.
 * CategoryInspector and DrawerPanel use the identical implementation via this hook.
 *
 * @param {React.RefObject} ref    — ref attached to the panel element
 * @param {boolean}         active — whether the trap is active
 * @param {Function}        onEscape — called when Escape is pressed
 * @param {React.RefObject} restoreFocusRef — optional explicit opener when layout focus precedes registration
 */
export function useFocusTrap(ref, active, onEscape, restoreFocusRef = null) {
  const onEscapeRef = useRef(onEscape);
  const layerTokenRef = useRef(Symbol('focus-layer'));

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!active || !ref.current) return undefined;
    const panel = ref.current;
    const layerToken = layerTokenRef.current;
    const previouslyFocused = restoreFocusRef?.current || document.activeElement;
    const focusFirst = () => {
      const [first] = getFocusableElements(panel);
      (first || panel).focus();
    };

    registerFocusLayer(layerToken);
    if (!panel.contains(document.activeElement)) focusFirst();

    const handleKey = (e) => {
      if (!isTopFocusLayer(layerToken)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onEscapeRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusableElements(panel);
      if (!focusable.length) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const handleFocusIn = (event) => {
      if (!isTopFocusLayer(layerToken)) return;
      if (!panel.contains(event.target)) focusFirst();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('focusin', handleFocusIn);
      unregisterFocusLayer(layerToken);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [active, ref, restoreFocusRef]);
}
